import React, {useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import {useTranslation} from "react-i18next";
import UserAvatar from "components/Common/UserAvatar";
import {
  PAGE_URLS,
  WATERSPEED_FAQ_LINK,
  WATERSPEED_PRIVACY_LINK,
  WATERSPEED_TERMS_LINK
} from "constants/AppConstants.js";
import {ws} from "styles/tokens";
import {useAppUser} from "contexts/AppUserContext";
import AuthSerive from "services/AuthService.js";
import LocalStorageService from "services/LocalStorageService.js";
import {StyleDiv} from "components/Common/StyleDiv";
import {StyleImg} from "components/Common/StyleImg";
import {StyleLink} from "components/Common/StyleLink";
import {StyleListItem, StyleUnorderedList} from "components/Common/StyleList";
import {registerPlugin} from "@capacitor/core";
import {isAndroid} from "FirebaseUtils";
import {useHistory, useLocation} from "react-router-dom";
import {isUserUltra, isUserPremium} from "services/FirebaseDbService";
import RemoteConfigService from "services/RemoteConfigService";
import {useColorSchemePreference} from "hooks/useColorScheme";
import type {ColorSchemePreference} from "services/ColorSchemeService";

const HelpCrunch: any = registerPlugin("HelpCrunchPlugin");
const UserAvatarAny: any = UserAvatar;

const COLOR_SCHEME_OPTIONS: {value: ColorSchemePreference; labelKey: string}[] = [
  {value: "light", labelKey: "settings.themeLight"},
  {value: "dark", labelKey: "settings.themeDark"},
  {value: "system", labelKey: "settings.themeSystem"}
];

interface Props {
  isNavDisabled?: boolean;
}

function ProfileImageModal({isNavDisabled}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const {appUser, user, claims} = useAppUser();
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const triggerRef = useRef<any>(null);
  const menuRef = useRef<any>(null);
  const closeMenu = () => setIsOpen(false);
  const [colorSchemePreference, setColorSchemePreference] = useColorSchemePreference();

  const canShowItems = (user: any) => {
    return user;
  };

  useEffect(() => {}, [isOpen, appUser]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event: any) => {
      if (menuRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <>
      <StyleDiv
        ref={triggerRef}
        margin="0px 5% 0px 0px"
        border={isOpen ? `2px solid ${ws.accent}` : "2px solid transparent"}
        borderRadius="50%"
        display="flex"
        cursor="pointer"
        zIndex="90000"
        opacity={isNavDisabled ? "0.6" : "1"}
        onClick={() => {
          if (!isNavDisabled) {
            setIsOpen((prevState) => {
              return !prevState;
            });
          }
        }}
      >
        <UserAvatarAny appUser={appUser} size="30" />
      </StyleDiv>
      {canShowItems(user) &&
        // Portalled: the nav's backdrop-filter would otherwise contain and clip this fixed menu.
        ReactDOM.createPortal(
          <StyleDiv
            ref={menuRef}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                closeMenu();
              }
            }}
            display={isOpen ? "block" : "none"}
            position="fixed"
            top="66px"
            right="16px"
            width="240px"
            padding="6px"
            borderRadius={ws.radiusMd}
            background={ws.card}
            border={`1px solid ${ws.line}`}
            boxShadow={ws.shadowMenu}
            zIndex="90000"
            transition="0.2s"
          >
            <StyleUnorderedList padding="0px 0px 0px 6px" zIndex="90000">
              <StyleListItem borderTop=" " padding="10px" zIndex="90000">
                <StyleImg width="15px" margin="0px 12px 0px 0px" src="/assets/icons/icon-user.svg" zIndex="90000" />
                <StyleLink to={PAGE_URLS.myAccountPageUrl} color={ws.fg} onClick={closeMenu}>
                  {t("account.myProfile")}{" "}
                  {appUser
                    ? (() => {
                        const ultraStatus = isUserUltra(appUser?.userInDb);
                        const premiumStatus = isUserPremium(appUser?.userInDb);
                        if (ultraStatus.isActive) {
                          return `(${RemoteConfigService.getSubscriptionTierName("ultra")})`;
                        } else if (premiumStatus.isActive) {
                          return `(${RemoteConfigService.getSubscriptionTierName("premium")})`;
                        } else {
                          return `(${t("account.freePlanLabel")})`;
                        }
                      })()
                    : ""}
                </StyleLink>
              </StyleListItem>
              {(!!claims?.admin || !!claims?.supervisor) && !isAndroid && (
                <StyleListItem padding="10px" zIndex="90000">
                  <StyleImg width="20px" margin="0px 10px 0px 0px" src="/assets/icons/icon-settings.svg" />
                  <StyleLink
                    to={`${PAGE_URLS.adminPageUrl}${PAGE_URLS.usersPageUrl}`}
                    color={ws.fg}
                    zIndex="90000"
                    onClick={closeMenu}
                  >
                    {t("nav.adminPortal")}
                  </StyleLink>
                </StyleListItem>
              )}
              <StyleListItem padding="10px" zIndex="90000">
                <StyleImg width="20px" margin="0px 10px 0px 0px" src="/assets/icons/icon-gift.svg" />
                <StyleLink to={PAGE_URLS.redeemCode} color={ws.fg} zIndex="90000" onClick={closeMenu}>
                  {t("androidSettings.redeemCode")}
                </StyleLink>
              </StyleListItem>
              <StyleListItem padding="10px" zIndex="90000">
                <StyleImg width="20px" margin="0px 10px 0px 0px" src="/assets/icons/icon-faq.svg" />
                <StyleLink to={{pathname: WATERSPEED_FAQ_LINK}} target="_blank" color={ws.fg} onClick={closeMenu}>
                  {t("settings.supportAndFaq")}
                </StyleLink>
              </StyleListItem>
              <StyleListItem padding="10px" zIndex="90000">
                <StyleImg width="20px" margin="0px 10px 0px 0px" src="/assets/icons/icon-accept.svg" zIndex="90000" />
                <StyleLink to={{pathname: WATERSPEED_PRIVACY_LINK}} target="_blank" color={ws.fg} onClick={closeMenu}>
                  {t("settings.privacyPolicy")}
                </StyleLink>
              </StyleListItem>
              <StyleListItem padding="10px" zIndex="0000">
                <StyleImg
                  width="15px"
                  margin="0px 10px 0px 0px"
                  src="/assets/icons/icon-insurance.svg"
                  zIndex="90000"
                />
                <StyleLink to={{pathname: WATERSPEED_TERMS_LINK}} target="_blank" color={ws.fg} onClick={closeMenu}>
                  {t("settings.termsAndConditions")}
                </StyleLink>
              </StyleListItem>
              {!isAndroid && (
                <StyleListItem padding="10px" zIndex="90000" display="block">
                  <StyleDiv color={ws.muted} fontSize="0.78rem" marginBottom="6px">
                    {t("settings.appearance")}
                  </StyleDiv>
                  <div className="ws-tabs" role="tablist" aria-label={t("settings.appearance")} style={{padding: 3}}>
                    {COLOR_SCHEME_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        role="tab"
                        aria-selected={colorSchemePreference === option.value}
                        onClick={() => setColorSchemePreference(option.value)}
                        style={{padding: "6px 12px", fontSize: "0.8rem"}}
                      >
                        {t(option.labelKey)}
                      </button>
                    ))}
                  </div>
                </StyleListItem>
              )}
              {isAndroid && (
                <StyleListItem padding="10px" zIndex="90000">
                  <StyleImg
                    width="18px"
                    margin="0px 10px 0px 0px"
                    src="/assets/icons/icon-information.svg"
                    zIndex="90000"
                  />
                  <StyleLink
                    to={"#"}
                    color={ws.fg}
                    onClick={async () => {
                      closeMenu();
                      try {
                        const result = await HelpCrunch.showChat();
                        console.log("HelpCrunch result:", result.value);
                      } catch (error) {
                        console.error("Error showing help crunch:", error);
                      }
                    }}
                  >
                    <strong>{t("settings.chatWithSupport")}</strong>
                  </StyleLink>
                </StyleListItem>
              )}
              <StyleListItem padding="10px" zIndex="90000">
                <StyleImg width="17px" margin="0px 12px 0px 0px" src="/assets/icons/icon-exit.svg" zIndex="90000" />
                <StyleLink
                  to={"#"}
                  color={ws.danger}
                  onClick={async () => {
                    closeMenu();
                    LocalStorageService.clear();
                    await AuthSerive.signOutAsync(isAndroid);
                    history.push(PAGE_URLS.signInPageUrl);
                  }}
                >
                  {t("account.logout")}
                </StyleLink>
              </StyleListItem>
            </StyleUnorderedList>
          </StyleDiv>,
          document.body
        )}
    </>
  );
}

export default ProfileImageModal;
